import { hijackWindowConsole } from './windowConsoleCapture'
import { captureWindowErrors } from './windowErrorCapture'
import { createStackTrace } from './stackTraceUtils'
import { WARNING_MESSAGES, groupByMaxAggregatedLength } from './utils'
import { spanIdHeaderKey, traceIdHeaderKey } from './traceidUtils'
import { CHAR_LIMIT, CLIENT_DEBOUNCE_PERIOD } from '@/constants/logging'
import { ClientLogBody, ClientLogSeverity } from '@/logging/ClientLogBody'
import { currentVersionName, isInLocalDevelopment } from '@/config/base'
import { debounce, isNil, isString } from '@/helpers/helpers'
import { getBase64Auth } from '@/helpers/network'
import { getCookies } from '@/helpers/cookieHelper'

export type PartialLogBody = Omit<ClientLogBody, 'appVersion' | 'clientUrl'>

let displayLogsInBrowserConsole = isInLocalDevelopment
/**
 * can get updated to display logs in the browser console. useful for debugging.
 */
export const setDisplayLogsInBrowserConsole = (display: boolean): void => {
  displayLogsInBrowserConsole = display
}

// shortcut for reportLog
export const reportMessage = (message: string, severity: ClientLogSeverity = 'info'): void =>
  reportLog({ message, severity, relatedTraceId: 'test' })

// eslint-disable-next-line unused-imports/no-unused-vars
export let reportLog = (item: PartialLogBody, rawConsoleLogArguments?: any[]): void => {} // will be replaced once the logging is setup

const extensionStackRegex = /-extension:|https:\/\/[a-z]+\.simscale\.com\/[a-zA-Z\d_]+:\d+:\d+/

export const setupLogging = (): void => {
  // create a queue for storing messages to be sent
  const queuedMessages: ClientLogBody[] = []

  const doCommitLog = (): void => {
    const cookie = getCookies()
    const headers = {
      'content-type': 'application/json',
      authorization: `Basic ${getBase64Auth(cookie?.uid, cookie?.sid)}`,
      'trace-id': cookie?.[traceIdHeaderKey] ?? undefined,
      'span-id': cookie?.[spanIdHeaderKey] ?? undefined
    }
    try {
      const stringifiedQueuedMessages = queuedMessages.map((message) => JSON.stringify(message))
      queuedMessages.length = 0
      // the messages in the queue have been processed.
      // the line above empties the queue of messages, so that new messages can be added to a fresh array.
      const groupedByMaxLength = groupByMaxAggregatedLength(stringifiedQueuedMessages, CHAR_LIMIT * 10)
      for (const group of groupedByMaxLength) {
        window
          .fetch('/projects/api/log/', {
            method: 'POST',
            headers,
            body: `[${group.join(',')}]`
          })
          .catch((error) => error)
      }
    } catch (e) {
      // error with the error logging request,
      // no logging here as logging this will probably create a infinite loop
    }
  }

  const debouncedCommitLog = debounce(doCommitLog, CLIENT_DEBOUNCE_PERIOD, { leading: true })

  let windowConsoleMethods: Pick<Console, 'log' | 'error' | 'warn' | 'debug'> & { info: Console['log'] }

  reportLog = (item: PartialLogBody, rawConsoleLogArguments?: any[]): void => {
    let message: any
    try {
      message = isString(item.message) ? item.message : JSON.stringify(item.message)
      message = message.slice(0, CHAR_LIMIT)
      // it can fail due to circular dependencies.
    } catch (e) {
      message = 'serialization failed.'
    }
    const stack_trace = item.stack_trace ?? createStackTrace(1)

    if (item.severity === 'error' && WARNING_MESSAGES.some((warningMessage) => message.includes(warningMessage))) {
      item.severity = 'warn'
      message = `Demoted error to warning: ${message}`
    }

    if (item.severity === 'error' && extensionStackRegex.test(stack_trace)) {
      item.severity = 'warn'
      message = `Demoted error to warning, extension stack trace identified. ${message}`
    }

    if (!isInLocalDevelopment) {
      queuedMessages.push({
        ...item,
        message,
        stack_trace,
        appVersion: currentVersionName,
        clientUrl: document.location.href
      })
      debouncedCommitLog()
    }

    if (displayLogsInBrowserConsole) {
      if (Array.isArray(rawConsoleLogArguments)) {
        windowConsoleMethods[item.severity](...rawConsoleLogArguments)
        return
      }
      const windowLogArguments = [
        message,
        !isNil(item.relatedTraceId) ? { relatedTraceId: item.relatedTraceId } : null,
        stack_trace
      ].filter((item) => !isNil(item))
      if (windowLogArguments.length > 1) {
        // eslint-disable-next-line no-console
        console.groupCollapsed(windowLogArguments[0])
        for (const arg of windowLogArguments.slice(1)) {
          windowConsoleMethods[item.severity](arg)
        }
        // eslint-disable-next-line no-console
        console.groupEnd()
      } else {
        windowConsoleMethods[item.severity](...windowLogArguments)
      }
    }
  }

  if (!isInLocalDevelopment) {
    windowConsoleMethods = hijackWindowConsole()
  } else {
    const { log, error, warn, debug } = window.console
    windowConsoleMethods = { log, error, warn, debug, info: log }
  }
  captureWindowErrors()

  // make sure to catch the last drop and send the queued messages before the window is closed
  window.addEventListener(
    'beforeunload',
    () => {
      if (queuedMessages.length > 0) {
        doCommitLog()
      }
    },
    false
  )
}
